import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import "./FullMobilePreview.css";

const FullMobilePreview = ({
    station,
    ads = [],
    currentSlideIndex = 0,
    setCurrentSlideIndex,
    isFullViewMode = true,
    onCloseFullView
}) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);

    // Audio & Player state
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [streamHealth, setStreamHealth] = useState("live"); // "connecting", "live", "error"

    // Device appearance state
    const [deviceSkin, setDeviceSkin] = useState("titanium"); // "titanium", "midnight", "silver"
    const [zoomScale, setZoomScale] = useState(1); // 1 = 100%, 0.85 = 85%, 0.7 = 70%
    const [currentTime, setCurrentTime] = useState("9:41");
    const [autoScrollSpeed, setAutoScrollSpeed] = useState(3000); // 3000ms
    const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
    const [currentAspectRatio, setCurrentAspectRatio] = useState(16 / 9);

    // Adaptively resize banner viewport according to natural image dimensions with area limit
    const handleBannerImageLoad = (e) => {
        const { naturalWidth, naturalHeight } = e.target;
        if (naturalWidth && naturalHeight) {
            const rawRatio = naturalWidth / naturalHeight;
            // Area limit clamp: between 1.15 (tallest safe vertical limit) and 2.35 (widest safe horizontal limit)
            const safeClampedRatio = Math.max(1.15, Math.min(2.35, rawRatio));
            setCurrentAspectRatio(safeClampedRatio);
        }
    };

    // Update real-time clock
    useEffect(() => {
        const updateClock = () => {
            const now = new Date();
            const hours = now.getHours();
            const minutes = now.getMinutes();
            const formatted = `${hours % 12 || 12}:${minutes < 10 ? "0" + minutes : minutes}`;
            setCurrentTime(formatted);
        };
        updateClock();
        const timer = setInterval(updateClock, 30000);
        return () => clearInterval(timer);
    }, []);

    // Resolve stream URL for live video player
    const isLocalStream = !station?.live ||
        station.live.includes("localhost") ||
        station.live.includes("127.0.0.1") ||
        station.live.startsWith("rtmp://");

    const hlsUrl = isLocalStream
        ? "http://localhost:8888/live/stream/index.m3u8"
        : (station?.hls || station?.live || "http://localhost:8888/live/stream/index.m3u8");

    // Initialize Hls.js video player inside the mobile screen
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        setStreamHealth("connecting");

        if (Hls.isSupported()) {
            if (hlsRef.current) {
                hlsRef.current.destroy();
            }

            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
                backBufferLength: 20,
                maxBufferLength: 8,
                liveSyncDurationCount: 3,
                liveMaxLatencyDurationCount: 6
            });

            hlsRef.current = hls;
            hls.loadSource(hlsUrl);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setStreamHealth("live");
                video.play().then(() => setIsPlaying(true)).catch(() => {
                    video.muted = true;
                    setIsMuted(true);
                    video.play().then(() => setIsPlaying(true)).catch(() => {});
                });
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            setStreamHealth("error");
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError();
                            break;
                        default:
                            hls.destroy();
                            setStreamHealth("error");
                            break;
                    }
                }
            });
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = hlsUrl;
            video.addEventListener("loadedmetadata", () => {
                video.play().then(() => setIsPlaying(true)).catch(() => {});
            });
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
            }
        };
    }, [hlsUrl, station?.id]);

    // Handle play / pause toggle
    const togglePlay = () => {
        if (!videoRef.current) return;
        if (isPlaying) {
            videoRef.current.pause();
            setIsPlaying(false);
        } else {
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
        }
    };

    // Handle mute / unmute toggle
    const toggleMute = () => {
        if (!videoRef.current) return;
        videoRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    // Filter active/enabled ads only (status !== 0)
    const activeAds = (ads || []).filter(ad => ad.status !== 0);

    // Auto-scroll banners timer
    useEffect(() => {
        if (activeAds.length <= 1 || isAutoScrollPaused) return;

        const interval = setInterval(() => {
            if (setCurrentSlideIndex) {
                setCurrentSlideIndex(prev => (prev + 1) % activeAds.length);
            }
        }, autoScrollSpeed);

        return () => clearInterval(interval);
    }, [activeAds.length, autoScrollSpeed, isAutoScrollPaused, setCurrentSlideIndex]);

    // Format poster URL
    const getPosterUrl = (posterPath) => {
        if (!posterPath) return "https://public.nanbateam.com/posters/1.jpg";
        if (posterPath.startsWith("http://") || posterPath.startsWith("https://") || posterPath.startsWith("data:image/")) {
            return posterPath;
        }
        return `https://public.nanbateam.com/${posterPath.replace(/^\/+/, '').replace(/^images\//, '')}`;
    };

    // Navigation handlers
    const handlePrevSlide = () => {
        if (!activeAds.length || !setCurrentSlideIndex) return;
        setCurrentSlideIndex(prev => (prev - 1 + activeAds.length) % activeAds.length);
    };

    const handleNextSlide = () => {
        if (!activeAds.length || !setCurrentSlideIndex) return;
        setCurrentSlideIndex(prev => (prev + 1) % activeAds.length);
    };

    // Social media links from station or fallbacks
    const socialLinks = station?.tv_social_medias || [];
    const getSocialLink = (code) => {
        const found = socialLinks.find(s => s.code?.trim().toLowerCase() === code || s["code "]?.trim().toLowerCase() === code);
        return found ? found.link : "#";
    };

    const stationPhone = station?.mobile || "9080736170";
    const currentAd = activeAds[currentSlideIndex] || activeAds[0];

    // Compute dimensions based on scale
    const baseWidth = isFullViewMode ? 390 : 290;
    const baseHeight = isFullViewMode ? 810 : 590;

    return (
        <div className="mobile-device-stage">
            {/* Remote Controls Toolbar (Only in Full View mode) */}
            {isFullViewMode && (
                <div className="mobile-device-remote-panel">
                    {/* Left: Device Controls */}
                    <div className="remote-control-group">
                        <span style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>
                            📱 Device Skin:
                        </span>
                        <button
                            className={`remote-btn ${deviceSkin === "titanium" ? "active" : ""}`}
                            onClick={() => setDeviceSkin("titanium")}
                        >
                            Titanium
                        </button>
                        <button
                            className={`remote-btn ${deviceSkin === "midnight" ? "active" : ""}`}
                            onClick={() => setDeviceSkin("midnight")}
                        >
                            Midnight
                        </button>
                        <button
                            className={`remote-btn ${deviceSkin === "silver" ? "active" : ""}`}
                            onClick={() => setDeviceSkin("silver")}
                        >
                            Silver
                        </button>
                    </div>

                    {/* Middle: Video & Audio Controls */}
                    <div className="remote-control-group">
                        <button
                            className={`remote-btn ${!isMuted ? "active" : ""}`}
                            onClick={toggleMute}
                            title="Toggle Audio"
                        >
                            {isMuted ? (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                                        <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                                        <line x1="12" y1="19" x2="12" y2="23"></line>
                                        <line x1="8" y1="23" x2="16" y2="23"></line>
                                    </svg>
                                    Unmute Stream Audio
                                </>
                            ) : (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                                    </svg>
                                    Audio Playing (Mute)
                                </>
                            )}
                        </button>
                        <button
                            className="remote-btn"
                            onClick={togglePlay}
                            title="Play/Pause Stream"
                        >
                            {isPlaying ? "Pause Stream" : "Play Stream"}
                        </button>
                    </div>

                    {/* Carousel Auto-scroll Controls */}
                    <div className="remote-control-group">
                        <span style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: 700 }}>
                            Carousel:
                        </span>
                        <button
                            className={`remote-btn ${isAutoScrollPaused ? "active" : ""}`}
                            onClick={() => setIsAutoScrollPaused(!isAutoScrollPaused)}
                            title="Pause/Resume Banner Auto-scroll"
                        >
                            {isAutoScrollPaused ? "▶ Resume" : "⏸ Pause"}
                        </button>
                        <button
                            className={`remote-btn ${autoScrollSpeed === 2000 ? "active" : ""}`}
                            onClick={() => setAutoScrollSpeed(2000)}
                        >
                            2s
                        </button>
                        <button
                            className={`remote-btn ${autoScrollSpeed === 3000 ? "active" : ""}`}
                            onClick={() => setAutoScrollSpeed(3000)}
                        >
                            3s
                        </button>
                        <button
                            className={`remote-btn ${autoScrollSpeed === 5000 ? "active" : ""}`}
                            onClick={() => setAutoScrollSpeed(5000)}
                        >
                            5s
                        </button>
                    </div>

                    {/* Right: Zoom Scale & Carousel speed */}
                    <div className="remote-control-group">
                        <span style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: 700 }}>
                            Scale:
                        </span>
                        <button
                            className={`remote-btn ${zoomScale === 1 ? "active" : ""}`}
                            onClick={() => setZoomScale(1)}
                        >
                            100%
                        </button>
                        <button
                            className={`remote-btn ${zoomScale === 0.85 ? "active" : ""}`}
                            onClick={() => setZoomScale(0.85)}
                        >
                            85%
                        </button>
                        <button
                            className={`remote-btn ${zoomScale === 0.72 ? "active" : ""}`}
                            onClick={() => setZoomScale(0.72)}
                        >
                            72%
                        </button>

                        {onCloseFullView && (
                            <button
                                className="remote-btn"
                                onClick={onCloseFullView}
                                style={{ background: "rgba(239, 68, 68, 0.2)", color: "#f87171", borderColor: "rgba(239, 68, 68, 0.4)", marginLeft: "8px" }}
                            >
                                ✕ Exit Full View
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Smartphone Hardware Chassis */}
            <div
                className={`smartphone-chassis skin-${deviceSkin}`}
                style={{
                    width: `${baseWidth}px`,
                    height: `${baseHeight}px`,
                    transform: isFullViewMode && zoomScale !== 1 ? `scale(${zoomScale})` : "none",
                    transformOrigin: "top center",
                    marginBottom: isFullViewMode && zoomScale !== 1 ? `-${baseHeight * (1 - zoomScale)}px` : "0"
                }}
            >
                {/* Physical Hardware Buttons */}
                <div className="chassis-btn volume-up"></div>
                <div className="chassis-btn volume-down"></div>
                <div className="chassis-btn power"></div>

                {/* Smartphone Display Screen */}
                <div className="smartphone-screen">
                    {/* Glass Glare Overlay */}
                    <div className="screen-glare-overlay"></div>

                    {/* Dynamic Island / Camera Notch */}
                    <div className="dynamic-island-notch">
                        <div className="notch-camera-lens"></div>
                        <div className="notch-sensor-dot"></div>
                    </div>

                    {/* Status Bar */}
                    <div className="phone-status-bar">
                        <span>{currentTime}</span>
                        <div className="status-bar-icons">
                            {/* Signal Bars */}
                            <svg width="15" height="11" viewBox="0 0 18 12" fill="currentColor">
                                <rect x="0" y="9" width="2.5" height="3" rx="0.5" />
                                <rect x="4" y="6" width="2.5" height="6" rx="0.5" />
                                <rect x="8" y="3.5" width="2.5" height="8.5" rx="0.5" />
                                <rect x="12" y="0.5" width="2.5" height="11.5" rx="0.5" />
                            </svg>
                            {/* 5G label */}
                            <span style={{ fontSize: "9px", fontWeight: 800 }}>5G</span>
                            {/* Wi-Fi Wave */}
                            <svg width="14" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M5 12.55a11 11 0 0 1 14.08 0"></path>
                                <path d="M1.42 9a16 16 0 0 1 21.16 0"></path>
                                <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
                                <line x1="12" y1="20" x2="12.01" y2="20"></line>
                            </svg>
                            {/* Battery Pill */}
                            <svg width="22" height="11" viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="1" y="1" width="18" height="10" rx="3" />
                                <rect x="3" y="3" width="12" height="6" rx="1.5" fill="#10b981" stroke="none" />
                                <path d="M21 4.5v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                    </div>

                    {/* App Header Bar (activity_nanban_tv.xml Header) */}
                    <div className="phone-app-header">
                        <div className="app-brand-info">
                            <div className="app-brand-logo">
                                {station?.logo ? (
                                    <img
                                        src={getPosterUrl(station.logo)}
                                        alt={station?.name || "Logo"}
                                        style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "8px" }}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                    />
                                ) : (
                                    station?.name ? station.name.substring(0, 2).toUpperCase() : "TV"
                                )}
                            </div>
                            <div>
                                <div className="app-brand-title">
                                    {station?.name || "Nanba TV"}
                                </div>
                                <div className="app-brand-subtitle">
                                    {station?.coverage || "Tamil Nadu Broadcast"}
                                </div>
                            </div>
                        </div>

                        <div className="live-onair-pill">
                            <span className="live-pulse-dot"></span>
                            <span>LIVE</span>
                        </div>
                    </div>

                    {/* LIVE VIDEO PLAYER (ExoPlayer simple_player emulation) */}
                    <div className="phone-video-player-container">
                        <video
                            ref={videoRef}
                            className="phone-video-element"
                            playsInline
                            muted={isMuted}
                            autoPlay
                        />

                        {/* Top Live Badge */}
                        <div className="player-overlay-badge">
                            <span style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor: streamHealth === "live" ? "#10b981" : "#eab308"
                            }}></span>
                            <span>{streamHealth === "live" ? "1080p • LIVE" : "Connecting..."}</span>
                        </div>

                        {/* Player Interactive Overlays */}
                        <div className="player-controls-overlay">
                            <button
                                className="player-icon-btn"
                                onClick={toggleMute}
                                title={isMuted ? "Unmute" : "Mute"}
                            >
                                {isMuted ? (
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                                    </svg>
                                ) : (
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                                    </svg>
                                )}
                            </button>
                            <button
                                className="player-icon-btn"
                                onClick={togglePlay}
                                title={isPlaying ? "Pause" : "Play"}
                            >
                                {isPlaying ? (
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                                        <rect x="6" y="4" width="4" height="16"></rect>
                                        <rect x="14" y="4" width="4" height="16"></rect>
                                    </svg>
                                ) : (
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* SOCIAL MEDIA BAR (social_media_layouts emulation) */}
                    <div className="phone-social-media-strip">
                        {/* WhatsApp */}
                        <a
                            href={getSocialLink("whatsapp") !== "#" ? getSocialLink("whatsapp") : `https://wa.me/${stationPhone}`}
                            target="_blank"
                            rel="noreferrer"
                            className="social-icon-btn whatsapp"
                            title="WhatsApp Channel"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981z"/>
                            </svg>
                        </a>

                        {/* YouTube */}
                        <a
                            href={getSocialLink("youtube") !== "#" ? getSocialLink("youtube") : "https://youtube.com"}
                            target="_blank"
                            rel="noreferrer"
                            className="social-icon-btn youtube"
                            title="YouTube Live"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                        </a>

                        {/* Instagram */}
                        <a
                            href={getSocialLink("instagram") !== "#" ? getSocialLink("instagram") : "https://instagram.com"}
                            target="_blank"
                            rel="noreferrer"
                            className="social-icon-btn instagram"
                            title="Instagram"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                            </svg>
                        </a>

                        {/* Facebook */}
                        <a
                            href={getSocialLink("facebook") !== "#" ? getSocialLink("facebook") : "https://facebook.com"}
                            target="_blank"
                            rel="noreferrer"
                            className="social-icon-btn facebook"
                            title="Facebook"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 8h-3v4h3v12h5V12h3.642L18 8h-4V6.333C14 5.374 14.5 5 15.658 5H18V0h-3.808C10.597 0 9 1.582 9 4.615V8z"/>
                            </svg>
                        </a>

                        {/* Twitter */}
                        <a
                            href={getSocialLink("twitter") !== "#" ? getSocialLink("twitter") : "https://twitter.com"}
                            target="_blank"
                            rel="noreferrer"
                            className="social-icon-btn twitter"
                            title="Twitter / X"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                        </a>

                        {/* Official Website */}
                        <a
                            href={station?.website ? (station.website.startsWith("http") ? station.website : `https://${station.website}`) : "https://nanbateam.com"}
                            target="_blank"
                            rel="noreferrer"
                            className="social-icon-btn website"
                            title="Official Website"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="2" y1="12" x2="22" y2="12"></line>
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                            </svg>
                        </a>
                    </div>

                    {/* BANNER CAROUSEL (TvAddsPagerAdapter / addRelative emulation) */}
                    <div className="phone-banner-carousel-container">
                        <div className="banner-carousel-header">
                            <span className="banner-carousel-title">
                                Sponsored Banners & Promos
                            </span>
                            {activeAds.length > 0 && (
                                <span className="banner-carousel-counter">
                                    {currentSlideIndex + 1} / {activeAds.length}
                                </span>
                            )}
                        </div>

                        <div
                            className="banner-viewport"
                            style={{
                                aspectRatio: `${currentAspectRatio}`,
                                maxHeight: isFullViewMode ? "220px" : "150px",
                                minHeight: isFullViewMode ? "110px" : "90px"
                            }}
                        >
                            {activeAds.length > 0 ? (
                                <>
                                    {/* Ambient Backdrop to fill letterbox margins on custom sizes */}
                                    <img
                                        src={getPosterUrl(currentAd?.poster)}
                                        alt=""
                                        className="banner-ambient-backdrop"
                                        aria-hidden="true"
                                    />

                                    <img
                                        src={getPosterUrl(currentAd?.poster)}
                                        alt={`Banner ${currentSlideIndex + 1}`}
                                        className="banner-slide-image"
                                        onLoad={handleBannerImageLoad}
                                        onError={(e) => {
                                            e.target.src = "http://localhost:8080/images/posters/1.jpg";
                                        }}
                                    />

                                    {/* Manual Navigation Arrows */}
                                    {activeAds.length > 1 && (
                                        <>
                                            <button
                                                className="carousel-nav-arrow left"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePrevSlide();
                                                }}
                                                aria-label="Previous Banner"
                                            >
                                                &#10094;
                                            </button>
                                            <button
                                                className="carousel-nav-arrow right"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleNextSlide();
                                                }}
                                                aria-label="Next Banner"
                                            >
                                                &#10095;
                                            </button>
                                        </>
                                    )}

                                    {/* Dots Indicator */}
                                    <div className="carousel-dots-strip" onClick={(e) => e.stopPropagation()}>
                                        {activeAds.map((_, idx) => (
                                            <span
                                                key={idx}
                                                className={`carousel-dot-indicator ${idx === currentSlideIndex ? "active" : ""}`}
                                                onClick={() => setCurrentSlideIndex && setCurrentSlideIndex(idx)}
                                            />
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div style={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "rgba(255, 255, 255, 0.4)",
                                    fontSize: "11px",
                                    gap: "6px"
                                }}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                        <polyline points="21 15 16 10 5 21"></polyline>
                                    </svg>
                                    <span>No Banners Uploaded</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* DOWNSITE SECTION: For Advertisement directly above Powered By */}
                    <div className="phone-bottom-section">
                        {/* FOR ADVERTISEMENT CALLOUT (directly above powered by) */}
                        <div className="advertisement-capsule-box">
                            <div className="ad-arrow-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8">
                                    <polyline points="15 18 9 12 15 6"></polyline>
                                </svg>
                            </div>

                            <div className="ad-capsule-content">
                                <div className="ad-capsule-title">FOR ADVERTISEMENT</div>
                                <a
                                    href={`tel:${stationPhone}`}
                                    className="ad-capsule-number"
                                >
                                    📞 {stationPhone}
                                </a>
                            </div>

                            <div className="ad-arrow-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </div>
                        </div>

                        {/* APP FOOTER BRANDING (downsite at the very bottom) */}
                        <div className="phone-footer-branding">
                            <div className="powered-by-text">
                                powered by : nanbateam.com
                            </div>
                            {station?.disclaimer && (
                                <div style={{ fontSize: "8.5px", color: "rgba(255, 255, 255, 0.4)", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {station.disclaimer}
                                </div>
                            )}
                            {/* Android Home Bar Indicator */}
                            <div className="home-gesture-indicator"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FullMobilePreview;
